import { Actions, Async, FSpread } from '@client/new/helpers/types';
import { useEffect, useState } from 'react';
import { isEmpty, omit } from 'lodash';
import { Influencer } from '@playbook-technologies/common-enums';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import {
  isOtpCreator,
  isSubscriptionCreator,
} from '@client/new/common/offering.helpers';
import {
  Feedback,
  getChecklistItems,
  getSubscriptionLinks,
  getOtpLinks,
  toFeedbackData,
  CreatorLinksType,
  getHomeCta,
  getOTPPrograms,
  getSubscriptionPrograms,
  SubscriptionTypeOptions,
  toSubscriptionEmptyStateOptionsByMode,
} from './mappers';
import { ButtonColor } from '@design-system/components/button/button.css';
import { browserHistory } from 'react-router';
import { PATHS } from '@client/routes';
import {
  Collections,
  Collection,
  LinksTab,
  CollectionType,
  SubscriptionType,
} from './home.domain';
import toast from 'react-hot-toast';
import { Offer, useHomeModel } from './home.model';
import { mixTrack } from '@services/mixpanel';

type HomeVM = {
  dispatch: HomeVMVDispatcher;
  headline: {
    title: string;
    link?: string;
    cta?: Array<{
      icon: IconProp;
      onClick: () => void;
      content?: string;
      className?: string;
      disabled?: boolean;
      color?: ButtonColor;
    }>;
  };
  isOnboarding: boolean;
  isLive: boolean;
  influencerId: number;
  uid: string;
  qrCodeModal: {
    open: boolean;
    onClose: () => void;
  };
  offerModal: {
    isOpen: boolean;
    onClose: () => void;
    collection: InitialCollection;
    setName: (value: string) => void;
    setType: (type: CollectionType) => void;
    subscriptionOptions: SubscriptionTypeOptions;
    createOffer: () => void;
    submitting: boolean;
    videoPlayed: (url: string) => void;
    offerTypeSelected: (type: CollectionType) => void;
    subscriptionTypeSelected: (type: SubscriptionType) => void;
  };
  educationCloseConfirmation: {
    isOpen: boolean;
    onClose: () => void;
    confirmClose: () => void;
  };
  goLiveModal: {
    isOpen: boolean;
    onClose: () => void;
    version: CollectionType | 'both';
    goLive: () => void;
    inProgress: boolean;
  };
  liveSuccessModal: {
    isOpen: boolean;
    onClose: () => void;
    version: CollectionType | 'both';
    influencerId: number;
  };
  onboarding: {
    checklist: Array<{
      title: string;
      done: boolean;
      to?: string;
      onClick?: () => void;
    }>;
    progres: number;
    completed: number;
  };
  links: {
    showLinks: boolean;
    activeTab: LinksTab;
    subscription: {
      show: boolean;
      links: CreatorLinksType;
    };
    otp: {
      show: boolean;
      options: Collections;
      selectedId?: number;
      links: CreatorLinksType;
    };
  };
  feedback: {
    show: boolean;
    data: Feedback;
  };
};

export type PayloadMap = {
  setLinkTab: LinksTab;
  selectOtp: Collection;
  closeModal: null;
  openQrCodeModal: null;
  setupOffer: null;
  setCollectionName: string;
  setCollectionType: CollectionType;
  openGoLiveModal: null;
  createOffer: null;
  goLive: null;
  backToEducation: null;
  confirmCloseEducation: null;
  openConfirmEducationClose: null;
  selectSubscriptionType: SubscriptionType;
  videoPlayed: string;
  offerTypeSelectedEvent: CollectionType;
  subscriptionTypeSelectedEvent: SubscriptionType;
};

export type HomeVMActions = Actions<PayloadMap>;
export type HomeVMVDispatcher = FSpread<PayloadMap>;

type ModalType = 'None' | 'QrCode' | 'Offer' | 'GoLive' | 'LiveSuccess';

export interface InitialCollection extends Omit<Offer, 'name'> {
  name?: string;
}

const initialCollection: InitialCollection = {
  name: undefined,
  type: 'otp',
  subscriptionType: undefined,
};
export const useHomeViewModel = (p: {
  influencerId?: string;
}): Async<HomeVM> => {
  const [modalType, setModalType] = useState<ModalType>('None');
  const [confirmEducationClose, setConfirmEducationClose] =
    useState<boolean>(false);
  const [initCollection, setInitCollection] =
    useState<InitialCollection>(initialCollection);
  const [linksState, setLinks] = useState<{
    selectedOTP?: Collection;
    activeTab: LinksTab;
  }>({
    selectedOTP: undefined,
    activeTab: 'subscription',
  });

  const asyncModel = useHomeModel(p);
  const homeData = asyncModel.type === 'fetched' ? asyncModel.value.data : {};

  useEffect(() => {
    if (
      asyncModel.type === 'fetched' &&
      asyncModel.value.data?.creator?.offering
    ) {
      const otpPrograms: Collections = getOTPPrograms(
        asyncModel.value.data.collections,
      );
      const tab = isSubscriptionCreator(
        asyncModel.value.data?.creator?.offering,
      )
        ? 'subscription'
        : 'otp';
      setLinks({ activeTab: tab, selectedOTP: otpPrograms[0] });
    }
    if (
      asyncModel.type === 'fetched' &&
      asyncModel.value.data?.creator?.customizations?.ppcConversionRequested
    ) {
      setInitCollection((prev) => ({ ...prev, subscriptionType: 'ppc' }));
    }
    if (asyncModel.type === 'fetched' && asyncModel.value.creatorWentLive) {
      setModalType('LiveSuccess');
    }
  }, [homeData]);

  if (asyncModel.type !== 'fetched') return asyncModel;

  const model = asyncModel.value;

  const dispatch: HomeVMVDispatcher = (type, ...args) => {
    const action = { type, payload: args[0] } as HomeVMActions;
    switch (action.type) {
      case 'setLinkTab':
        handleTabChange(action.payload);
        return null;
      case 'selectOtp':
        setLinks((prev) => ({ ...prev, selectedOTP: action.payload }));
        return null;
      case 'openQrCodeModal':
        setModalType('QrCode');
        return null;
      case 'closeModal':
        setModalType('None');
        return null;
      case 'setupOffer':
        setupOffer();
        return null;
      case 'openGoLiveModal':
        setModalType('GoLive');
        return null;
      case 'setCollectionName':
        setInitCollection((prev) => ({ ...prev, name: action.payload }));
        return null;
      case 'setCollectionType':
        setInitCollection((prev) => ({ ...prev, type: action.payload }));
        return null;
      case 'createOffer':
        createOffer();
        return null;
      case 'goLive':
        model.dispatch('goLive');
        return null;
      case 'confirmCloseEducation':
        setModalType('None');
        setConfirmEducationClose(false);
        setInitCollection(initialCollection);
        return null;
      case 'backToEducation':
        setConfirmEducationClose(false);
        return null;
      case 'openConfirmEducationClose':
        setConfirmEducationClose(true);
        return null;
      case 'selectSubscriptionType':
        setInitCollection((prev) => ({
          ...prev,
          subscriptionType: action.payload,
        }));
        return null;
      case 'offerTypeSelectedEvent':
        mixTrack({
          type:
            action.payload === 'otp'
              ? 'OTP has been selected within our Go Live Modal Flow'
              : 'Subscription has been selected within our Go Live Modal Flow',
          payload: {},
        });
        return null;
      case 'subscriptionTypeSelectedEvent':
        mixTrack({
          type:
            action.payload === 'ppc'
              ? 'PPC has been selected within our Go Live Modal Flow'
              : 'Platform has been selected within our Go Live Modal Flow',
          payload: {},
        });
        return null;
      case 'videoPlayed':
        mixTrack({
          type: 'Education Video Watched',
          payload: {
            'Video Watched': action.payload,
          },
        });
        return null;
    }
  };

  const influencer = model.data?.creator;
  const isLive = model.data?.creator?.mode === Influencer.Mode.Live.value;
  const isOnboarding =
    model.data?.creator?.mode === Influencer.Mode.Onboarding.value;
  const onboardingList = omit(model.data?.onboardingListData, '__typename');
  const customURL = influencer?.customURL || undefined;
  const profileDeepLink = influencer?.profileDeepLink || undefined;
  const otpPrograms: Collections = getOTPPrograms(model.data.collections);
  const subscriptionPrograms: Collections = getSubscriptionPrograms(
    model.data.collections,
  );
  const isPpcRequested = !!influencer?.customizations?.ppcConversionRequested;
  const isPPC = influencer?.visibility === 'exclusive';

  const createOffer = async () => {
    if (!initCollection.name) {
      toast.error('Collection name is missing');
      return;
    }
    model.dispatch('createOffer', initCollection as Offer);
  };

  const setupOffer = () => {
    if (isEmpty(model.data.collections)) {
      setModalType('Offer');
      return;
    }
    browserHistory.push(
      PATHS.PROGRAM_EDIT(model.data.collections[0].id, model.influencerId),
    );
  };

  const handleTabChange = (tab: LinksTab) => {
    setLinks((prev) => {
      const selectedOTP =
        !prev.selectedOTP && !isEmpty(otpPrograms)
          ? otpPrograms[0]
          : prev.selectedOTP;
      return { ...prev, selectedOTP, activeTab: tab };
    });
  };

  const checklist = getChecklistItems({
    data: onboardingList,
    creatorId: model.influencerId,
    email: influencer?.email || undefined,
    isLive,
    dispatch,
  });
  const checklistCompleted = checklist.filter((i) => i.done).length;
  const checklistProgress = Math.ceil((checklistCompleted * 100) / 3);

  const canGoLive = Object.values(onboardingList).every((i) => i === true);
  const goLiveVersion =
    !isEmpty(otpPrograms) && !isEmpty(subscriptionPrograms)
      ? 'both'
      : !isEmpty(subscriptionPrograms)
      ? 'subscription'
      : 'otp';

  return {
    type: 'fetched',
    value: {
      dispatch,
      headline: {
        title: `Welcome ${influencer?.firstName || ''} 👋`,
        link: isLive
          ? 'https://docs.google.com/document/d/1ufQNOy-6-v5VfnPDrDEVJqIPCV_ILOtPY-wGM2ZakbE/preview'
          : undefined,
        cta: getHomeCta({ isLive, dispatch, canGoLive }),
      },
      isOnboarding,
      isLive,
      influencerId: model.influencerId,
      uid: model.uid,
      onboarding: {
        checklist,
        progres: checklistProgress,
        completed: checklistCompleted,
      },
      links: {
        showLinks: isLive,
        activeTab: linksState.activeTab,
        subscription: {
          show: isSubscriptionCreator(model?.data?.creator?.offering),
          links: getSubscriptionLinks(isPPC, customURL, profileDeepLink),
        },
        otp: {
          show: isOtpCreator(model?.data?.creator?.offering),
          options: otpPrograms,
          selectedId: linksState.selectedOTP?.id,
          links: getOtpLinks({
            customURL,
            selectedOTP: linksState.selectedOTP,
          }),
        },
      },
      qrCodeModal: {
        open: modalType === 'QrCode',
        onClose: () => dispatch('closeModal'),
      },
      offerModal: {
        isOpen: modalType === 'Offer',
        onClose: () => dispatch('openConfirmEducationClose'),
        collection: initCollection,
        setName: (value: string) => dispatch('setCollectionName', value),
        setType: (type: CollectionType) => dispatch('setCollectionType', type),
        subscriptionOptions: toSubscriptionEmptyStateOptionsByMode({
          onSelect: (type: SubscriptionType) =>
            dispatch('selectSubscriptionType', type),
          disabledType: isPpcRequested ? 'platform' : undefined,
        }),
        createOffer: () => dispatch('createOffer'),
        submitting: model.submittingOffer,
        videoPlayed: (url: string) => dispatch('videoPlayed', url),
        offerTypeSelected: (type: CollectionType) =>
          dispatch('offerTypeSelectedEvent', type),
        subscriptionTypeSelected: (type: SubscriptionType) =>
          dispatch('subscriptionTypeSelectedEvent', type),
      },
      educationCloseConfirmation: {
        isOpen: confirmEducationClose,
        onClose: () => dispatch('backToEducation'),
        confirmClose: () => dispatch('confirmCloseEducation'),
      },
      goLiveModal: {
        isOpen: modalType === 'GoLive',
        onClose: () => dispatch('closeModal'),
        version: goLiveVersion,
        goLive: () => dispatch('goLive'),
        inProgress: model.goLiveProgress,
      },
      liveSuccessModal: {
        isOpen: modalType === 'LiveSuccess',
        onClose: () => dispatch('closeModal'),
        version: goLiveVersion,
        influencerId: model.influencerId,
      },
      feedback: {
        show: isLive,
        data: toFeedbackData(model.data.searchComments),
      },
    },
  };
};
